// Shared message normalization used by ChatPanel and RelationPanel so both
// panels unwrap backend responses (and history rows) identically.

export function createMessageId(prefix = 'msg') {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return `${prefix}-${crypto.randomUUID()}`;
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function normalizeMessage(message, fallbackRole = 'assistant') {
  if (!message || typeof message !== 'object') return null;

  const role = message.role || fallbackRole;
  const content =
    message.content ??
    message.answer ??
    message.message ??
    message.text ??
    '';
  // Prefer any stable DB-provided identifier over generating a client-side
  // one, so the React key (and dedupe logic below) stays stable across a
  // history refresh instead of getting a fresh random id every load.
  const dbId = message.id ?? message._id ?? message.message_id ?? message.messageId;

  return {
    ...message,
    id: dbId ?? createMessageId(role === 'user' ? 'user' : 'assistant'),
    role,
    content,
    loading: Boolean(message.loading),
  };
}

export function normalizeBackendResponse(result) {
  if (!result || typeof result !== 'object') return [];

  const userMessage = normalizeMessage(result.user_message, 'user');
  const assistantSource =
    result.assistant_message ||
    result.message ||
    result.assistant ||
    result;

  // Spread every field the backend sent (not an allow-list) so any new
  // response field survives normalization without a frontend change.
  const assistantMessage = assistantSource && typeof assistantSource === 'object'
    ? normalizeMessage(
        {
          ...assistantSource,
          role: 'assistant',
          content: assistantSource.content ?? assistantSource.answer ?? assistantSource.message ?? '',
          loading: false,
        },
        'assistant'
      )
    : null;

  return [userMessage, assistantMessage].filter(Boolean);
}

// Collapses messages sharing the same id down to one entry, keeping the
// position of the first occurrence (order) and the data of the last
// occurrence (most complete/authoritative).
function dedupeMessagesById(list) {
  const latestById = new Map();
  list.forEach(m => { if (m && m.id != null) latestById.set(m.id, m); });
  const seen = new Set();
  const result = [];
  list.forEach(m => {
    if (!m || m.id == null || seen.has(m.id)) return;
    seen.add(m.id);
    result.push(latestById.get(m.id));
  });
  return result;
}

export function sanitizeMessages(list) {
  if (!Array.isArray(list)) return [];
  const normalized = list.flatMap(item => {
    // History rows are normally already flat single messages, but if the
    // backend ever returns a per-turn pair (matching the send-response shape)
    // instead, route it through the same unwrapping used for sent messages
    // so the user question and assistant table/chart aren't dropped.
    if (item && typeof item === 'object' && !item.role && (item.user_message || item.assistant_message)) {
      return normalizeBackendResponse(item);
    }
    const single = normalizeMessage(item, item?.role || 'assistant');
    return single ? [single] : [];
  });
  return dedupeMessagesById(normalized);
}
